import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';

const s = {
  wrap: {
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2
  },
  title: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: '#7fb3ff',
    letterSpacing: 1
  },
  scroller: {
    maxHeight: 260,
    minHeight: 140,
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.25)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 8,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  msgRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    padding: '4px 0',
    position: 'relative'
  },
  author: {
    fontWeight: 700,
    fontSize: 12,
    color: '#7fb3ff',
    flexShrink: 0
  },
  authorMine: { color: '#ffb347' },
  body: { fontSize: 13, color: '#dde', wordBreak: 'break-word', whiteSpace: 'pre-wrap', flex: 1 },
  time: { fontSize: 10, color: '#556', marginLeft: 4 },
  delBtn: {
    background: 'none',
    border: 'none',
    color: '#f44336',
    fontSize: 13,
    cursor: 'pointer',
    opacity: 0.5,
    padding: '0 4px',
    lineHeight: 1
  },
  empty: {
    textAlign: 'center',
    color: '#445',
    fontSize: 13,
    padding: '24px 10px'
  },
  inputRow: { display: 'flex', gap: 8 },
  input: {
    flex: 1,
    padding: '9px 12px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 7,
    color: '#fff',
    fontSize: 14,
    outline: 'none'
  },
  sendBtn: {
    padding: '9px 16px',
    borderRadius: 7,
    border: 'none',
    background: 'linear-gradient(135deg, #D50A0A, #a00808)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer'
  },
  counter: { fontSize: 11, color: '#556', marginTop: 2, textAlign: 'right' }
};

function formatTime(iso) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PoolChat({ poolId, user }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const scrollerRef = useRef(null);
  const lastSeenId = useRef(0);
  const autoScroll = useRef(true);

  const loadMessages = useCallback(async () => {
    if (!poolId) return;
    try {
      // Incremental fetch: only pull newer than lastSeenId
      const since = lastSeenId.current || 0;
      const res = await API.get(`/pools/${poolId}/messages`, { params: { since } });
      const incoming = res.data.messages || [];
      if (incoming.length === 0) return;
      setMessages(prev => {
        // If this is a full load (since=0), replace; otherwise append
        if (since === 0) return incoming;
        // De-dupe in case of timing
        const ids = new Set(prev.map(m => m.id));
        const merged = [...prev, ...incoming.filter(m => !ids.has(m.id))];
        return merged;
      });
      lastSeenId.current = Math.max(lastSeenId.current, ...incoming.map(m => m.id));
    } catch (e) {
      // silent; keep polling
    }
  }, [poolId]);

  // Reset when pool changes
  useEffect(() => {
    setMessages([]);
    lastSeenId.current = 0;
    loadMessages();
  }, [poolId, loadMessages]);

  // Poll every 3s
  useEffect(() => {
    const iv = setInterval(loadMessages, 3000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  // Auto-scroll to bottom on new messages (unless user scrolled up)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (autoScroll.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    autoScroll.current = nearBottom;
  };

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setErr(null);
    try {
      const res = await API.post(`/pools/${poolId}/messages`, { message: body });
      setText('');
      if (res.data?.message) {
        setMessages(prev => {
          if (prev.some(m => m.id === res.data.message.id)) return prev;
          return [...prev, res.data.message];
        });
        lastSeenId.current = Math.max(lastSeenId.current, res.data.message.id);
      }
      autoScroll.current = true;
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const del = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await API.delete(`/pools/${poolId}/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.title}>💬 POOL CHAT</div>
        <div style={{ fontSize: 11, color: '#556' }}>
          {messages.length > 0 ? `${messages.length} message${messages.length === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      <div style={s.scroller} ref={scrollerRef} onScroll={onScroll}>
        {messages.length === 0 ? (
          <div style={s.empty}>No messages yet. Start the trash talk 🔥</div>
        ) : (
          messages.map(m => {
            const mine = m.user_id === user.id || m.username === user.username;
            const canDelete = mine || user.is_admin;
            return (
              <div key={m.id} style={s.msgRow}>
                <span style={{ ...s.author, ...(mine ? s.authorMine : {}) }}>
                  {m.username}:
                </span>
                <span style={s.body}>{m.message}</span>
                <span style={s.time}>{formatTime(m.created_at)}</span>
                {canDelete && (
                  <button style={s.delBtn} onClick={() => del(m.id)} title="Delete message">✕</button>
                )}
              </div>
            );
          })
        )}
      </div>

      <div style={s.inputRow}>
        <input
          style={s.input}
          placeholder="Say something..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          maxLength={500}
          disabled={sending}
        />
        <button style={{ ...s.sendBtn, opacity: sending || !text.trim() ? 0.6 : 1 }} onClick={send} disabled={sending || !text.trim()}>
          Send
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#ff8888' }}>⚠️ {err}</div>}
      {text.length > 400 && <div style={s.counter}>{text.length}/500</div>}
    </div>
  );
}
